import React from "react";
import { Edit3 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cleanMarkdownText } from "@/lib/markdown-utils";
import { cn } from "@/lib/utils";
import { UnifiedMarkdownRenderer } from "@/components/ui/unified-markdown-renderer";
import { useTranslation } from "react-i18next";

interface ProcessedTextDisplayProps {
  processedText: string | null;
  onEditStart: () => void;
  isEditable?: boolean;
  className?: string;
}

export const ProcessedTextDisplay: React.FC<ProcessedTextDisplayProps> = ({
  processedText,
  onEditStart,
  isEditable = true,
  className = "",
}) => {
  const { t } = useTranslation();
  const cleanedText = cleanMarkdownText(processedText);
  const [isMobile, setIsMobile] = React.useState(false);

  React.useEffect(() => {
    const checkMobile = () => setIsMobile(window.innerWidth < 768);
    checkMobile();
    window.addEventListener("resize", checkMobile);
    return () => window.removeEventListener("resize", checkMobile);
  }, []);

  if (!cleanedText) {
    return (
      <div className="text-muted-foreground italic bg-muted/30 p-4 rounded-md text-center">
        {t('records.processed_text.no_content')}
      </div>
    );
  }

  return (
    <div className={cn("relative group", className)}>
      {/* Edit button - visible on hover for desktop, always visible on mobile */}
      {isEditable && (
        <Button
          variant="ghost"
          size="sm"
          onClick={onEditStart}
          className={cn(
            "absolute top-2 right-2 p-2 z-10 rounded-lg",
            "bg-background/80 backdrop-blur-sm border border-border/20 shadow-sm",
            "opacity-0 group-hover:opacity-100 transition-all duration-200",
            "hover:bg-background/90 hover:shadow-md focus:opacity-100 focus:bg-background/90",
            "focus:outline-none focus:ring-2 focus:ring-brand-500",
            isMobile ? "opacity-100" : ""
          )}
          aria-label={t('records.processed_text.edit_aria')}
        >
          <Edit3 size={16} className="text-muted-foreground hover:text-brand-600" />
        </Button>
      )}

      {/* Unified Markdown content */}
      <div className="w-full max-w-none">
        <UnifiedMarkdownRenderer
          value={cleanedText}
          mode="view"
          readOnly={true}
          height={300}
          className="border-none shadow-none"
        />
      </div>
    </div>
  );
};